import { reportError } from "@cabocheck/shared";
import IframeResizer from "@iframe-resizer/react";
import type { LoaderFunctionArgs } from "react-router";
import { data } from "react-router";
import { Link, useLoaderData } from "react-router";
import invariant from "invariant";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { client } from "~/client/client";
import { Button } from "~/components/ui/button";
import { PublicCalendar } from "~/features/calendar/public/PublicCalendar";
import { ParameterFaqs } from "~/features/products/ParameterFaqs";
import { ProductBanner } from "~/features/products/ProductBanner";
import { ProductBenefits } from "~/features/products/ProductBenefits";
import { getMedicalTestProfileTranslated } from "~/utils/apis/loadProducts.server";
import { getBaseParams } from "~/utils/getBaseParams";

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { name, slug } = params;

  if (!name) throw new Response("Not found", { status: 404 });
  invariant(slug, "slug is required");

  const baseParams = await getBaseParams(request, { withDeviceGuid: false });

  const { data: responseData, error } = await client.v1.getApiPartnerLandingByCode({
    ...baseParams,
    code: slug,
  });

  if (error || !responseData.response) {
    reportError("Product request failed", error);
    throw new Response("Something went wrong", { status: 500 });
  }

  const partnerLandingResponse = responseData.response;

  const summary = partnerLandingResponse.medical_test_profiles?.find(
    (product) => product.name === name && product.selling_price != null,
  );

  if (!summary) throw new Response("Not found", { status: 404 });

  const detail = await client.v1.getApiPartnerLandingMedicalTestProfile(summary.medical_test_profile_guid, {
    ...baseParams,
    code: slug,
  });
  if (detail.error || !detail.data?.response) {
    throw new Response("Could not load product", { status: detail.error?.error === "not_found" ? 404 : 500 });
  }
  const product = detail.data.response;

  const translatedProduct = getMedicalTestProfileTranslated({
    profile: product,
    device_locale: baseParams.device_locale,
  });

  return data({
    product: translatedProduct,
    partnerLanding: {
      ...partnerLandingResponse,
      medical_test_profiles: partnerLandingResponse.medical_test_profiles?.filter(
        (profile) => profile.selling_price != null,
      ),
    },
  });
};

export default function Index() {
  const { product, partnerLanding } = useLoaderData<typeof loader>();
  const parameters = product.translated.parameters;

  const { t } = useTranslation();

  return (
    <div className="public-container pt-20" id="angebot">
      <Link to="./../" className="mt-12 text-center text-dynamic-primary sm:text-left">
        {t("Zurück zur Testübersicht")}
      </Link>
      <ProductBanner product={product} />
      {/* <AquireTest /> */}
      <div className="h-20" />
      {/* <ProductInformatives informatives={product.informatives} /> */}
      <div className="flex flex-col gap-12">
        {partnerLanding?.iframe ? (
          partnerLanding.show_iframe_as_url ? (
            <a href={partnerLanding.iframe} target="_blank" rel="noreferrer" className="w-full sm:w-auto">
              <Button
                endIcon={<ArrowRight />}
                size="lg"
                className="w-full max-w-xs whitespace-normal break-words text-center leading-snug sm:w-auto sm:max-w-none"
              >
                {t("Hier klicken um zum Terminbuchungssystem zu gelangen")}
              </Button>
            </a>
          ) : (
            <IframeResizer
              license="GPLv3"
              log="collapsed"
              src={partnerLanding.iframe}
              style={{ width: "100%", height: "100vh" }}
            />
          )
        ) : (
          <PublicCalendar partnerLanding={partnerLanding} medicalTestProfileGuid={product.medical_test_profile_guid} />
        )}
        <ProductBenefits />
        {parameters && parameters.length > 0 ? (
          <div className="grid gap-8">
            {parameters.map((parameter) => (
              <ParameterFaqs key={parameter.name} parameter={parameter} />
            ))}
          </div>
        ) : null}
      </div>
      <div className="h-12" />
    </div>
  );
}
